import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { EventForm, AGE_WIRE_MAP } from '../components/events/EventForm';
import type { EventFormValues } from '../components/events/EventForm';
import {
  useCreateEventDraft,
  useCreateEvent,
  useGetStripeAccountStatus,
  type SingleEventCreationPayload,
  type EventTicketRequest,
  type MediaUrlEntry,
} from '../api/events';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { resolveDominantColorForSubmit } from '../utils/dominantColor';
import { apiClient } from '../api/api';
import { FiArrowLeft } from 'react-icons/fi';
import { toast } from '../utils/toast';
import { saveEventSuccess } from '../utils/eventSuccess';
import { extractApiError } from '../utils/apiError';
import { buildGuestPayloadFields } from '../utils/guestInvite';
import { nowAsPicked, wallClockAsPicked } from '../utils/eventDate';

export const Route = createFileRoute('/events/create/single')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: CreateSingleEventPage,
});

function CreateSingleEventPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createDraft = useCreateEventDraft();
  const createEvent = useCreateEvent();
  const { data: stripeAccount } = useGetStripeAccountStatus({ enabled: true });
  const stripeIsReady = Boolean(stripeAccount?.can_receive_payments);

  const buildPayload = (values: EventFormValues) => {
    const start = wallClockAsPicked(values.startDate, values.startTime);
    const eventEndIso = wallClockAsPicked(values.endDate, values.endTime);

    const eventPayload: SingleEventCreationPayload = {
      event_title: values.event_title,
      event_type: 'physical',
      event_age: AGE_WIRE_MAP[values.event_age] || values.event_age,
      info: values.info,
      location_body: values.locationBody
        ? {
            street: values.locationBody.address,
            city: values.locationBody.city,
            state: values.locationBody.state,
            country: values.locationBody.country,
            longitude: values.locationBody.longitude,
            latitude: values.locationBody.latitude,
          }
        : { street: '', city: '', state: '', country: '', longitude: 0, latitude: 0 },
      genres: values.genres,
      start,
      end: eventEndIso,
      sponsors: '',
      email: '',
      address:
        values.locationDetails || values.locationBody?.address || values.locationBody?.city || '',
      collaborators: values.collaboratorIds.length ? values.collaboratorIds : undefined,
      is_private: values.is_private ?? false,
      iana_timezone: values.timezone,
    };

    if (values.is_private) {
      Object.assign(eventPayload, buildGuestPayloadFields(values.guests));
    }

    const tickets: EventTicketRequest[] = (values.tickets ?? []).map((t) => {
      const ticketReq: EventTicketRequest = {
        id: 0,
        ticket_type: t.ticket_type,
        price: t.price,
        ticket_number_issued: t.quantity,
        set_purchase_date: t.has_end_purchase_period ?? false,
        is_multi_event_pass: t.multi_day_access || false,
        start_at:
          t.go_live_on_publish || !t.sales_start_date || !t.sales_start_time
            ? nowAsPicked()
            : wallClockAsPicked(t.sales_start_date, t.sales_start_time),
        expired_at:
          t.has_end_purchase_period && t.sales_end_date && t.sales_end_time
            ? wallClockAsPicked(t.sales_end_date, t.sales_end_time)
            : wallClockAsPicked(values.endDate, values.endTime),
      };
      if (t.description) {
        ticketReq.description = t.description;
      }
      if (t.sub_events) {
        ticketReq.access_type = 'all_access';
        ticketReq.accessible_sub_event_ids = [];
      }
      if (t.go_live_on_publish) {
        ticketReq.use_publish_time_for_start_at = true;
      }
      return ticketReq;
    });

    return { event: eventPayload, ticket: tickets };
  };

  const handleSubmit = async (values: EventFormValues, action: 'save' | 'publish') => {
    const hasPaidTicket = (values.tickets ?? []).some((t) => t.price > 0);
    const dominantColor = await resolveDominantColorForSubmit(
      values.images[0],
      values.dominantColor,
    );

    if (action === 'publish' && hasPaidTicket && !stripeIsReady) {
      const basePayload = buildPayload(values);
      basePayload.event.dominant_color = dominantColor;

      let mediaUrls: MediaUrlEntry[] = [];
      const newFiles = values.imageFiles.filter((f): f is File => f !== null);
      if (newFiles.length > 0) {
        try {
          const results = await uploadFilesToCloudinary(newFiles, {
            folder: 'event_images',
            uploadPreset: 'event_preset',
          });
          mediaUrls = results.map((r) => ({
            public_id: r.public_id,
            secure_url: r.secure_url,
            resource_type: r.resource_type,
            bytes: r.bytes,
            format: r.format,
          }));
        } catch {
          toast.error('Failed to upload images. Please try again.');
          return;
        }
      }

      const payload = { ...basePayload, media_urls: mediaUrls.length > 0 ? mediaUrls : undefined };

      createDraft.mutate(payload, {
        onSuccess: () => {
          toast.info('Event saved as draft. Please set up payment to publish.');
          queryClient.invalidateQueries({ queryKey: ['events'] });
          navigate({ to: '/profile/settings/payments' });
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create event draft'));
        },
      });
      return;
    }

    // Validate @username existence for private event guests
    if (values.is_private && values.guests?.length) {
      const usernames = values.guests
        .filter((g) => g.email.startsWith('@'))
        .map((g) => g.email.slice(1));
      for (const username of usernames) {
        try {
          const { data } = await apiClient.get(
            `/custom_account/username-availability/?username=${encodeURIComponent(username)}`,
          );
          if (data?.available === true) {
            toast.error(`User "@${username}" does not exist.`);
            return;
          }
        } catch {
          toast.error('Failed to validate guest usernames. Please try again.');
          return;
        }
      }
    }

    const basePayload = buildPayload(values);
    basePayload.event.dominant_color = dominantColor;

    // Upload new images to Cloudinary
    let mediaUrls: MediaUrlEntry[] = [];
    const newFiles = values.imageFiles.filter((f): f is File => f !== null);
    if (newFiles.length > 0) {
      try {
        const results = await uploadFilesToCloudinary(newFiles, {
          folder: 'event_images',
          uploadPreset: 'event_preset',
        });
        mediaUrls = results.map((r) => ({
          public_id: r.public_id,
          secure_url: r.secure_url,
          resource_type: r.resource_type,
          bytes: r.bytes,
          format: r.format,
        }));
      } catch {
        toast.error('Failed to upload images. Please try again.');
        return;
      }
    }

    const payload = { ...basePayload, media_urls: mediaUrls.length > 0 ? mediaUrls : undefined };

    if (action === 'publish') {
      createEvent.mutate(payload, {
        onSuccess: (data) => {
          toast.success('Event created successfully!');
          queryClient.invalidateQueries({ queryKey: ['events'] });
          const successId = saveEventSuccess(
            data,
            values.locationBody ?? null,
            mediaUrls[0]?.secure_url,
          );
          if (successId) {
            navigate({
              to: '/events/$eventId/success',
              params: { eventId: successId },
              replace: true,
            });
          }
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create event'));
        },
      });
    } else {
      createDraft.mutate(payload, {
        onSuccess: (data) => {
          toast.success('Event draft saved!');
          queryClient.invalidateQueries({ queryKey: ['events'] });
          const eventId = data.event_id ?? data.event?.id;
          if (eventId) {
            navigate({
              to: '/events/$eventId',
              params: { eventId: String(eventId) },
              replace: true,
            });
          }
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to create event'));
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20">
        <div className="max-w-360 mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={24}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>

        <EventForm
          mode="create"
          title="New Event"
          statusBadge="draft"
          onSubmit={handleSubmit}
          isLoading={createDraft.isPending || createEvent.isPending}
        />
      </main>

      <BottomNav />
    </div>
  );
}
